/**
 * View steps after certificate upload (step 6) for non-accountant owner onboarding.
 * Everyone gets CAE (step 7). CFE import is only for tenants that already had e-invoicing.
 *
 * ## Why this is a list and not arithmetic
 *
 * These indices used to be written as `fiscal + 1`, `personalize + 2`, and so on.
 * That shape makes inserting a step a multi-line edit where every later line has
 * to be bumped in lockstep, and getting it wrong does not fail to compile — the
 * user lands on a blank screen, because a consumer is rendering an index nothing
 * maps to. That exact failure already happened once here and had to be reverted.
 *
 * So the ORDER is now the single source of truth: `POST_CERT_STEP_ORDER` below is
 * the running order, positions are allocated from it, and a step that is not
 * applicable to this tenant takes no number and reports `null`.
 *
 * **To add a step: add one entry to `POST_CERT_STEP_ORDER`, in the position it
 * runs, and add its key to `OwnerPostCertViewSteps`.** Nothing else in this file
 * needs to change. (Consumers still do — the render branch in
 * `OnboardingStepContent.tsx` and the step labels in `getOnboardingStepLabels.ts`
 * — but they key off the NAME, so they cannot silently drift out of alignment the
 * way bare numbers did.)
 */

/**
 * The CFE-import step imports comprobantes from a PREVIOUS provider, so it runs
 * only when the tenant is migrating. It used to also fire for every
 * already_issuer, but a already_issuer who is NOT migrating (reopening / already
 * has their own cert) has no prior CFEs to import — the situation step now lets
 * them choose, persisting migratingFromAnotherPlatform accordingly.
 */
export function getNeedsCfeImportStep(
  migratingFromAnotherPlatform: boolean | null | undefined
): boolean {
  return Boolean(migratingFromAnotherPlatform);
}

export type OwnerPostCertViewSteps = {
  cae: number;
  cfe: number | null;
  fiscal: number;
  literalE: number | null;
  personalize: number;
  /**
   * Migration checklist when migrating, or plan selection when not migrating.
   */
  migrationOrPlanFirst: number;
  /** Plan selection when migrating (after migration checklist). */
  planMigrating: number;
};

/** Certificate upload. The post-cert steps are numbered from here. */
const CERT_STEP = 6;

/** Inputs that decide which optional steps run for this tenant. */
type StepConditions = {
  needsCfeImportStep: boolean;
};

/**
 * The running order of the post-cert steps.
 *
 * `applies` omitted means the step always runs. A step whose `applies` returns
 * false consumes no position, so every later step shifts down by one — which is
 * precisely the arithmetic this file used to spell out by hand.
 */
const POST_CERT_STEP_ORDER: ReadonlyArray<{
  key: keyof OwnerPostCertViewSteps;
  applies?: (c: StepConditions) => boolean;
}> = [
  { key: 'cae' },
  { key: 'cfe', applies: (c) => c.needsCfeImportStep },
  { key: 'fiscal' },
  { key: 'literalE' },
  { key: 'personalize' },
  { key: 'migrationOrPlanFirst' },
  { key: 'planMigrating' },
];

/**
 * @param needsCfeImportStep - whether the CFE-import step runs for this tenant
 * @param preCertOffset - pre-DGI steps inserted before this tail (2 for the self
 *   path, which runs régimen + plan before situation; 0 otherwise). Shifts every
 *   post-cert position so the tail stays contiguous after the renumber.
 */
export function getOwnerPostCertViewSteps(
  needsCfeImportStep: boolean,
  preCertOffset = 0
): OwnerPostCertViewSteps {
  const conditions: StepConditions = { needsCfeImportStep };

  const positions = {} as Record<keyof OwnerPostCertViewSteps, number | null>;
  let next = CERT_STEP + preCertOffset + 1;
  for (const step of POST_CERT_STEP_ORDER) {
    const runs = step.applies ? step.applies(conditions) : true;
    positions[step.key] = runs ? next++ : null;
  }

  // The cast is confined to this one return: every key in the type is present in
  // POST_CERT_STEP_ORDER, and only the two declared `| null` keys can be null.
  return positions as OwnerPostCertViewSteps;
}

/**
 * Extra view steps inserted after cert (6) before fiscal: always +1 (CAE),
 * optionally +1 (CFE). Derived from the order above rather than restated, so it
 * cannot fall out of step with it.
 */
export function getOwnerExtraStepsAfterCert(needsCfeImportStep: boolean): number {
  const steps = getOwnerPostCertViewSteps(needsCfeImportStep);
  return steps.fiscal - CERT_STEP - 1;
}
